import { useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';

const INITIAL_PAGE_SIZE = 500;
const BACKGROUND_PAGE_SIZE = 500;
const BACKGROUND_CONCURRENCY = 4;

const LIST_QUERY = {
    sort: { field: 'customId', order: 'ASC' },
    filter: {},
};

const pointKey = point => {
    if (point?.id != null && point.id !== '') return `id:${point.id}`;
    return `fallback:${point?.customId ?? ''}:${point?.latitude ?? ''}:${point?.longitude ?? ''}`;
};

const fetchCollectionPointPage = (dataProvider, page, perPage) =>
    dataProvider.getList('collection-point', {
        ...LIST_QUERY,
        pagination: { page, perPage },
    });

export const useCollectionPointMapPoints = () => {
    const dataProvider = useDataProvider();
    const [points, setPoints] = useState([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const seen = new Set();
        setPoints([]);
        setError(null);
        setIsInitialLoading(true);
        setIsLoadingMore(false);

        const appendUnique = incoming => {
            if (cancelled) return;
            const fresh = [];
            (incoming ?? []).forEach(point => {
                const key = pointKey(point);
                if (seen.has(key)) return;
                seen.add(key);
                fresh.push(point);
            });
            if (fresh.length === 0) return;
            setPoints(current => (cancelled ? current : current.concat(fresh)));
        };

        const loadRemaining = async totalCount => {
            const pageCount = Math.ceil(totalCount / BACKGROUND_PAGE_SIZE);
            let nextPageIndex = 0;
            let stop = false;
            let backgroundError = null;

            const worker = async () => {
                while (!stop && !cancelled) {
                    const pageIndex = nextPageIndex;
                    nextPageIndex += 1;
                    if (pageIndex >= pageCount) return;

                    try {
                        const { data } = await fetchCollectionPointPage(dataProvider, pageIndex + 1, BACKGROUND_PAGE_SIZE);
                        if (cancelled || stop) return;
                        appendUnique(data);
                    } catch (pageError) {
                        if (!cancelled) backgroundError = pageError;
                        stop = true;
                    }
                }
            };

            const workerCount = Math.min(BACKGROUND_CONCURRENCY, pageCount);
            await Promise.all(Array.from({ length: workerCount }, () => worker()));
            if (!cancelled && backgroundError) setError(backgroundError);
        };

        const load = async () => {
            try {
                const first = await fetchCollectionPointPage(dataProvider, 1, INITIAL_PAGE_SIZE);
                if (cancelled) return;

                const firstData = first.data ?? [];
                const parsedTotal = Number(first.total);
                const totalCount = Number.isFinite(parsedTotal) ? parsedTotal : firstData.length;
                appendUnique(firstData);
                setIsInitialLoading(false);

                if (firstData.length >= totalCount) return;

                setIsLoadingMore(true);
                // Later pages use the same page size. Offset is (page - 1) * perPage,
                // so the first background page overlaps the initial 500 records; appendUnique drops those duplicates.
                await loadRemaining(totalCount);
            } catch (loadError) {
                if (cancelled) return;
                setError(loadError);
                setIsInitialLoading(false);
            } finally {
                if (!cancelled) setIsLoadingMore(false);
            }
        };

        void load();

        return () => {
            cancelled = true;
        };
    }, [dataProvider]);

    return { points, isInitialLoading, isLoadingMore, error };
};
